import { useCallback, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Alert } from '../../components/ui/Alert'
import { Toast } from '../../components/ui/Toast'
import { SkeletonCard } from '../../components/ui/Skeleton'
import * as categoryService from '../../services/categoryService'
import * as storefrontAi from '../../services/storefrontAiService'
import * as storeApi from '../../services/storeService'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { uploadProductImages } from '../../services/uploadService'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

/** Must match API `STOREFRONT_TEXT_LIMITS` in shopora_Api `store.dto.ts`. */
const STOREFRONT_LIMITS = {
  heroTitle: 100,
  heroSubtitle: 240,
  heroCta: 80,
  heroBadge: 80,
  trustLabel: 80,
  trustSublabel: 120,
} as const

const textareaFieldClass =
  'w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all duration-150 placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60 min-h-[88px] resize-y'

function CharCount({ len, max }: { len: number; max: number }) {
  return (
    <p className="mt-1 text-xs tabular-nums text-muted-foreground">
      {len}/{max}
    </p>
  )
}

const TRUST_ICON_OPTIONS = ['Truck', 'ShieldCheck', 'RotateCcw', 'Clock', 'Package'] as const

function newSlide(): storeApi.StorefrontHeroSlide {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `slide-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  return {
    id,
    image: '',
    title: '',
    subtitle: '',
    ctaText: 'Shop now',
    badge: null,
    ctaHref: '',
  }
}

function newBadge(): storeApi.StorefrontTrustBadge {
  return { icon: 'Truck', label: '', sublabel: '' }
}

/** Category slug from `/products?category=` when CTA is a product-list link; empty = all products. */
function parseCategorySlugFromCta(ctaHref: string | null | undefined): string {
  const raw = (ctaHref ?? '').trim()
  if (raw === '' || raw === '/products') return ''
  try {
    const u = new URL(raw, 'http://localhost')
    if (u.pathname !== '/products') return ''
    const cat = u.searchParams.get('category')
    return cat ? decodeURIComponent(cat).trim().toLowerCase() : ''
  } catch {
    return ''
  }
}

function ctaHrefFromCategoryDropdown(slug: string): string | null {
  const s = slug.trim().toLowerCase()
  if (!s) return null
  return `/products?category=${encodeURIComponent(s)}`
}

function normalizeForApi(content: storeApi.StorefrontContentPayload): storeApi.StorefrontContentPayload {
  return {
    heroSlides: content.heroSlides.map((s) => ({
      ...s,
      badge: s.badge === '' || s.badge == null ? null : s.badge,
      ctaHref: s.ctaHref === '' || s.ctaHref == null ? null : s.ctaHref,
      subtitle: s.subtitle ?? '',
    })),
    trustBadges: content.trustBadges.map((b) => ({
      ...b,
      sublabel: b.sublabel ?? '',
    })),
  }
}

export default function StorefrontEditor() {
  const { currentStoreId: storeId, storeRecord, loading, refreshStores } = useCurrentStore()
  const [heroSlides, setHeroSlides] = useState<storeApi.StorefrontHeroSlide[]>([])
  const [trustBadges, setTrustBadges] = useState<storeApi.StorefrontTrustBadge[]>([])
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null)
  const [uploadingSlideId, setUploadingSlideId] = useState<string | null>(null)
  const [categoryRows, setCategoryRows] = useState<categoryService.CategoryRow[]>([])
  /** `slide.id -> false` means collapsed; missing key = expanded */
  const [slideExpanded, setSlideExpanded] = useState<Record<string, boolean>>({})
  const [aiHeroBusySlideId, setAiHeroBusySlideId] = useState<string | null>(null)
  const [aiTrustBusy, setAiTrustBusy] = useState(false)
  const [aiImageSlideId, setAiImageSlideId] = useState<string | null>(null)

  useEffect(() => {
    if (!storeRecord) {
      setHeroSlides([])
      setTrustBadges([])
      return
    }
    const sc = storeRecord.storefrontContent
    if (sc && typeof sc === 'object') {
      setHeroSlides(Array.isArray(sc.heroSlides) ? sc.heroSlides : [])
      setTrustBadges(Array.isArray(sc.trustBadges) ? sc.trustBadges : [])
    } else {
      setHeroSlides([])
      setTrustBadges([])
    }
  }, [storeRecord])

  useEffect(() => {
    let cancelled = false
    if (storeId == null) {
      setCategoryRows([])
      return
    }
    categoryService
      .fetchCategories({ page: 1, limit: 100 })
      .then((res) => {
        if (cancelled) return
        const storeCats = res.items.filter((c) => c.source === 'store' && c.id != null)
        setCategoryRows(storeCats)
      })
      .catch(() => {
        if (!cancelled) setCategoryRows([])
      })
    return () => {
      cancelled = true
    }
  }, [storeId])

  const handleSave = async () => {
    if (storeId == null) return
    setSaveError(null)
    setSaving(true)
    try {
      const payload = normalizeForApi({ heroSlides, trustBadges })
      await storeApi.updateStore(storeId, { storefrontContent: payload })
      setToast({ message: 'Storefront saved', variant: 'success' })
      await refreshStores()
    } catch (e) {
      setSaveError(getUserFacingErrorMessage(e, 'Save failed'))
      setToast({ message: getUserFacingErrorMessage(e, 'Save failed'), variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const onUploadHeroImage = async (slideId: string, files: FileList | null) => {
    if (!files?.length) return
    setUploadingSlideId(slideId)
    try {
      const urls = await uploadProductImages(files)
      const url = urls[0]
      if (!url) return
      setHeroSlides((prev) => prev.map((s) => (s.id === slideId ? { ...s, image: url } : s)))
    } catch (e) {
      setSaveError(getUserFacingErrorMessage(e, 'Image upload failed'))
    } finally {
      setUploadingSlideId(null)
    }
  }

  const runAiImagePoll = async (jobId: string, slideId: string) => {
    setAiImageSlideId(slideId)
    try {
      for (let i = 0; i < 90; i += 1) {
        const st = await storefrontAi.getStorefrontAiJob(jobId)
        if (st.status === 'completed') {
          setHeroSlides((prev) =>
            prev.map((s) => (s.id === slideId ? { ...s, image: st.imageUrl } : s)),
          )
          setToast({ message: 'Hero banner image ready', variant: 'success' })
          return
        }
        if (st.status === 'failed') {
          setToast({
            message: st.error || 'Hero image generation failed',
            variant: 'error',
          })
          return
        }
        await new Promise((r) => setTimeout(r, 2000))
      }
      setToast({ message: 'Hero image generation timed out', variant: 'error' })
    } catch (e) {
      setToast({
        message: getUserFacingErrorMessage(e, 'Could not load generated image'),
        variant: 'error',
      })
    } finally {
      setAiImageSlideId(null)
    }
  }

  const handleHeroAiForSlide = async (
    slideId: string,
    /** New slides are not always in `heroSlides` yet (e.g. Add slide with AI); use for category / CTA context. */
    slideHint?: storeApi.StorefrontHeroSlide,
  ) => {
    if (storeId == null) return
    const store = storeRecord
    if (!store) return
    const slideForAi = heroSlides.find((s) => s.id === slideId) ?? slideHint
    const slugRaw = slideForAi ? parseCategorySlugFromCta(slideForAi.ctaHref) : ''
    const slug = slugRaw === '' ? null : slugRaw
    setSaveError(null)
    setAiHeroBusySlideId(slideId)
    try {
      const res = await storefrontAi.draftStorefrontAi({
        scope: 'hero',
        targetSlideId: slideId,
        promotedCategorySlug: slug,
      })
      if (!res.heroPatch) {
        setToast({ message: 'AI did not return hero copy', variant: 'error' })
        return
      }
      const patch = res.heroPatch
      let merged: storeApi.StorefrontHeroSlide | null = null
      flushSync(() => {
        setHeroSlides((prev) => {
          const i = prev.findIndex((s) => s.id === slideId)
          if (i === -1) return prev
          merged = { ...prev[i], ...patch }
          const next = [...prev]
          next[i] = merged
          return next
        })
      })
      setToast({ message: 'AI copy applied to this slide.', variant: 'success' })
      if (merged && !String(merged.image ?? '').trim()) {
        const catMeta = slug ? categoryRows.find((c) => c.slug.toLowerCase() === slug) : null
        const { jobId } = await storefrontAi.startHeroImageJob({
          storeName: store.name,
          heroTitle: merged.title,
          heroSubtitle: merged.subtitle ?? '',
          categoryName: catMeta?.name ?? null,
          visualHints: null,
        })
        void runAiImagePoll(jobId, slideId)
      }
    } catch (e) {
      const msg = getUserFacingErrorMessage(e, 'AI generation failed')
      setSaveError(msg)
      setToast({ variant: 'error', message: msg })
    } finally {
      setAiHeroBusySlideId(null)
    }
  }

  const handleTrustAi = async () => {
    setSaveError(null)
    setAiTrustBusy(true)
    try {
      const res = await storefrontAi.draftStorefrontAi({ scope: 'trust' })
      const tb = res.storefrontContent?.trustBadges
      if (!tb || !Array.isArray(tb)) {
        setToast({ message: 'AI did not return trust badges', variant: 'error' })
        return
      }
      setTrustBadges(tb)
      setToast({ message: 'Trust badges updated from AI.', variant: 'success' })
    } catch (e) {
      const msg = getUserFacingErrorMessage(e, 'AI generation failed')
      setSaveError(msg)
      setToast({ variant: 'error', message: msg })
    } finally {
      setAiTrustBusy(false)
    }
  }

  const handleAddSlideWithAi = async () => {
    if (heroSlides.length >= 8) return
    const s = newSlide()
    flushSync(() => {
      setSlideExpanded((prev) => ({ ...prev, [s.id]: true }))
      setHeroSlides((prev) => [...prev, s])
    })
    await handleHeroAiForSlide(s.id, s)
  }

  const removeSlide = (slideId: string) => {
    setHeroSlides((prev) => prev.filter((s) => s.id !== slideId))
    setSlideExpanded((prev) => {
      const next = { ...prev }
      delete next[slideId]
      return next
    })
  }

  const toggleSlideCollapsed = (slideId: string) => {
    setSlideExpanded((prev) => {
      if (prev[slideId] === false) {
        const next = { ...prev }
        delete next[slideId]
        return next
      }
      return { ...prev, [slideId]: false }
    })
  }

  const moveSlide = (index: number, dir: -1 | 1) => {
    setHeroSlides((prev) => {
      const next = [...prev]
      const j = index + dir
      if (j < 0 || j >= next.length) return prev
      ;[next[index], next[j]] = [next[j], next[index]]
      return next
    })
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonCard className="h-40" />
        <SkeletonCard className="h-64" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {toast ? (
        <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />
      ) : null}

      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Storefront</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Homepage hero carousel and trust badges shown on your customer storefront.
        </p>
      </div>

      {loadError ? <Alert variant="destructive">{loadError}</Alert> : null}

      {!loading && !storeRecord ? (
        <Alert variant="warning">No store found for your organisation. Create a store from registration or support.</Alert>
      ) : null}

      {saveError ? <Alert variant="destructive">{saveError}</Alert> : null}

      <Card>
        <CardHeader
          title="Homepage hero"
          description="Create a hero carousel for your storefront: add slides with images and messaging, send each button to a category or custom link, and collapse slides you are not editing to keep the page tidy."
        />
        <div className="space-y-6 px-6 pb-6">
          {heroSlides.map((slide, index) => {
            const expanded = slideExpanded[slide.id] !== false
            const slugVal = parseCategorySlugFromCta(slide.ctaHref)
            const slugInList = categoryRows.some((c) => c.slug.toLowerCase() === slugVal)

            return (
            <div
              key={slide.id}
              className="rounded-2xl border border-border bg-card/40 p-4 shadow-sm"
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <button
                    type="button"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
                    onClick={() => toggleSlideCollapsed(slide.id)}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse slide' : 'Expand slide'}
                  >
                    <span className="text-xs">{expanded ? '▼' : '▶'}</span>
                  </button>
                  <div className="min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Slide {index + 1}
                    </span>
                    {!expanded ? (
                      <p className="truncate text-sm text-foreground">
                        {slide.title.trim() || '— Empty title —'}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="text-xs"
                    disabled={
                      aiTrustBusy || aiHeroBusySlideId !== null || aiImageSlideId === slide.id
                    }
                    onClick={() => void handleHeroAiForSlide(slide.id)}
                  >
                    {aiHeroBusySlideId === slide.id ? 'AI…' : 'Generate copy (AI)'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="text-xs"
                    disabled={index === 0}
                    onClick={() => moveSlide(index, -1)}
                  >
                    Up
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="text-xs"
                    disabled={index >= heroSlides.length - 1}
                    onClick={() => moveSlide(index, 1)}
                  >
                    Down
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    className="text-xs"
                    disabled={aiHeroBusySlideId === slide.id || aiImageSlideId === slide.id}
                    onClick={() => removeSlide(slide.id)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
              {expanded ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Image</label>
                  {slide.image ? (
                    <div className="space-y-2">
                      <img
                        src={slide.image}
                        alt=""
                        className="max-h-40 w-full rounded-lg object-cover object-center"
                      />
                      <label className="inline-flex cursor-pointer">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                          disabled={uploadingSlideId === slide.id || aiImageSlideId === slide.id}
                          onChange={(e) => void onUploadHeroImage(slide.id, e.target.files)}
                        />
                        <span className="text-sm text-primary underline">
                          {aiImageSlideId === slide.id
                            ? 'Generating AI image…'
                            : uploadingSlideId === slide.id
                              ? 'Uploading…'
                              : 'Replace image'}
                        </span>
                      </label>
                    </div>
                  ) : (
                    <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-border py-8 text-sm text-muted-foreground hover:bg-muted/40">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        disabled={uploadingSlideId === slide.id || aiImageSlideId === slide.id}
                        onChange={(e) => void onUploadHeroImage(slide.id, e.target.files)}
                      />
                      {aiImageSlideId === slide.id
                        ? 'Generating AI banner image…'
                        : uploadingSlideId === slide.id
                          ? 'Uploading…'
                          : 'Click to upload image'}
                    </label>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <Input
                      id={`hero-title-${slide.id}`}
                      label="Title"
                      maxLength={STOREFRONT_LIMITS.heroTitle}
                      value={slide.title}
                      onChange={(e) =>
                        setHeroSlides((prev) =>
                          prev.map((s) => (s.id === slide.id ? { ...s, title: e.target.value } : s)),
                        )
                      }
                    />
                    <CharCount len={slide.title.length} max={STOREFRONT_LIMITS.heroTitle} />
                  </div>
                  <div>
                    <label
                      htmlFor={`hero-subtitle-${slide.id}`}
                      className="mb-2 block text-sm font-medium text-foreground"
                    >
                      Subtitle
                    </label>
                    <textarea
                      id={`hero-subtitle-${slide.id}`}
                      className={textareaFieldClass}
                      maxLength={STOREFRONT_LIMITS.heroSubtitle}
                      value={slide.subtitle}
                      onChange={(e) =>
                        setHeroSlides((prev) =>
                          prev.map((s) => (s.id === slide.id ? { ...s, subtitle: e.target.value } : s)),
                        )
                      }
                      rows={4}
                    />
                    <CharCount len={slide.subtitle.length} max={STOREFRONT_LIMITS.heroSubtitle} />
                  </div>
                  <div>
                    <Input
                      id={`hero-badge-${slide.id}`}
                      label="Badge (optional)"
                      maxLength={STOREFRONT_LIMITS.heroBadge}
                      value={slide.badge ?? ''}
                      onChange={(e) =>
                        setHeroSlides((prev) =>
                          prev.map((s) => (s.id === slide.id ? { ...s, badge: e.target.value || null } : s)),
                        )
                      }
                    />
                    <CharCount len={(slide.badge ?? '').length} max={STOREFRONT_LIMITS.heroBadge} />
                  </div>
                  <div>
                    <Input
                      id={`hero-cta-${slide.id}`}
                      label="CTA label"
                      maxLength={STOREFRONT_LIMITS.heroCta}
                      value={slide.ctaText}
                      onChange={(e) =>
                        setHeroSlides((prev) =>
                          prev.map((s) => (s.id === slide.id ? { ...s, ctaText: e.target.value } : s)),
                        )
                      }
                    />
                    <CharCount len={slide.ctaText.length} max={STOREFRONT_LIMITS.heroCta} />
                  </div>
                  <Select
                    id={`hero-category-${slide.id}`}
                    label="Featured category for button (optional)"
                    value={slugVal}
                    disabled={storeId == null}
                    onChange={(e) => {
                      const v = e.target.value.trim().toLowerCase()
                      const nextHref = v === '' ? null : ctaHrefFromCategoryDropdown(v)
                      setHeroSlides((prev) =>
                        prev.map((s) =>
                          s.id === slide.id
                            ? { ...s, ctaHref: nextHref == null ? '' : nextHref }
                            : s,
                        ),
                      )
                    }}
                  >
                    <option value="">All products</option>
                    {slugVal !== '' && !slugInList ? (
                      <option value={slugVal}>Current link (saved: {slugVal})</option>
                    ) : null}
                    {categoryRows.map((c) => {
                      const slug = c.slug.toLowerCase()
                      return (
                        <option key={c.id ?? slug} value={slug}>
                          {c.name} ({c.slug})
                        </option>
                      )
                    })}
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sends shoppers to that category on your product list. Use the field below for a
                    custom URL instead.
                  </p>
                  <Input
                    label="CTA URL (optional)"
                    placeholder="/products or https://…"
                    value={slide.ctaHref ?? ''}
                    onChange={(e) =>
                      setHeroSlides((prev) =>
                        prev.map((s) => (s.id === slide.id ? { ...s, ctaHref: e.target.value } : s)),
                      )
                    }
                  />
                </div>
              </div>
              ) : null}
            </div>
            )
          })}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setHeroSlides((prev) => [...prev, newSlide()])}
              disabled={heroSlides.length >= 8 || aiHeroBusySlideId !== null || aiTrustBusy}
            >
              Add slide
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleAddSlideWithAi()}
              disabled={heroSlides.length >= 8 || aiHeroBusySlideId !== null || aiTrustBusy}
            >
              Add slide with AI
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Trust badges"
          description="Short reassurance row under the hero (up to six items). Generate trust badges (AI) replaces all badges with a new set (hero slides are unchanged)."
        />
        <div className="border-b border-border px-6 pb-4">
          <Button
            type="button"
            disabled={aiTrustBusy || aiHeroBusySlideId !== null || storeId == null}
            onClick={() => void handleTrustAi()}
          >
            {aiTrustBusy ? 'Working…' : 'Generate trust badges (AI)'}
          </Button>
        </div>
        <div className="space-y-4 px-6 pb-6">
          {trustBadges.map((b, index) => (
            <div
              key={index}
              className="grid gap-3 rounded-2xl border border-border bg-card/40 p-4 md:grid-cols-[140px_1fr_1fr_auto]"
            >
              <Select
                id={`trust-icon-${index}`}
                label="Icon"
                value={b.icon}
                onChange={(e) =>
                  setTrustBadges((prev) =>
                    prev.map((row, i) => (i === index ? { ...row, icon: e.target.value } : row)),
                  )
                }
              >
                {TRUST_ICON_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
              <div>
                <Input
                  id={`trust-label-${index}`}
                  label="Label"
                  maxLength={STOREFRONT_LIMITS.trustLabel}
                  value={b.label}
                  onChange={(e) =>
                    setTrustBadges((prev) =>
                      prev.map((row, i) => (i === index ? { ...row, label: e.target.value } : row)),
                    )
                  }
                />
                <CharCount len={b.label.length} max={STOREFRONT_LIMITS.trustLabel} />
              </div>
              <div>
                <Input
                  id={`trust-sublabel-${index}`}
                  label="Sublabel"
                  maxLength={STOREFRONT_LIMITS.trustSublabel}
                  value={b.sublabel}
                  onChange={(e) =>
                    setTrustBadges((prev) =>
                      prev.map((row, i) => (i === index ? { ...row, sublabel: e.target.value } : row)),
                    )
                  }
                />
                <CharCount len={b.sublabel.length} max={STOREFRONT_LIMITS.trustSublabel} />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="danger"
                  className="w-full md:w-auto"
                  onClick={() => setTrustBadges((prev) => prev.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() => setTrustBadges((prev) => [...prev, newBadge()])}
            disabled={trustBadges.length >= 6}
          >
            Add badge
          </Button>
        </div>
      </Card>

      <div className="flex justify-end pb-8">
        <Button type="button" onClick={() => void handleSave()} disabled={saving || storeId == null}>
          {saving ? 'Saving…' : 'Save storefront'}
        </Button>
      </div>
    </div>
  )
}
